import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '../../hooks/useUser';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../sp-firebase/firebase';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

interface Ingestion {
  id: number;
  account_id: number | null;
  account_name: string | null;
  worker_name: string | null;
  gmail_message_id: string;
  from_address: string | null;
  to_address: string | null;
  subject: string | null;
  received_at: string | null;
  window_key: string | null;
  status: string;
  batch_id: number | null;
  attachment_count: number;
  error: string | null;
}

interface IngestionFile {
  id: number;
  original_filename: string;
  canonical_role: string | null;
  content_type: string | null;
  size_bytes: number | null;
  status: string;
}

interface Alias {
  id: number;
  account_id: number;
  account_name: string;
  alias: string;
  worker_name: string;
  allowed_senders: string[] | null;
  active: boolean;
  notes: string | null;
}

const STATUS_COLORS: Record<string, string> = {
  imported: 'bg-green-100 text-green-800',
  incomplete: 'bg-yellow-100 text-yellow-800',
  received: 'bg-blue-100 text-blue-800',
  quarantined: 'bg-orange-100 text-orange-800',
  failed: 'bg-red-100 text-red-800',
  ignored: 'bg-gray-100 text-gray-600',
};

const STATUS_FILTERS = ['', 'received', 'incomplete', 'imported', 'quarantined', 'failed'];

const EmailIngestions: React.FC = () => {
  const { data: userData } = useUser();
  const navigate = useNavigate();

  const [ingestions, setIngestions] = useState<Ingestion[]>([]);
  const [aliases, setAliases] = useState<Alias[]>([]);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [files, setFiles] = useState<Record<number, IngestionFile[]>>({});
  const [showAliasForm, setShowAliasForm] = useState(false);
  const [aliasForm, setAliasForm] = useState({ accountId: '', alias: '', workerName: 'strauss', allowedSenders: '', notes: '' });

  useEffect(() => {
    if (userData && !userData.isAdmin) navigate('/');
  }, [userData, navigate]);

  const token = async () => {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    return user.getIdToken();
  };

  const api = useCallback(async (path: string, init?: { method?: string; body?: string; headers?: Record<string, string> }) => {
    const t = await token();
    const res = await fetch(`${API_URL}/api/v2/email-ingest${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}`, ...(init?.headers || {}) },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || data.details || `Request failed (${res.status})`);
    return data;
  }, []);

  const fetchIngestions = useCallback(async () => {
    setLoading(true);
    try {
      const q = statusFilter ? `?status=${statusFilter}` : '';
      const data = await api(`/${q}`);
      setIngestions(data.ingestions || []);
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter]);

  const fetchAliases = useCallback(async () => {
    try {
      const data = await api('/aliases');
      setAliases(data.aliases || []);
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message });
    }
  }, [api]);

  useEffect(() => {
    if (userData?.isAdmin) {
      fetchIngestions();
      fetchAliases();
    }
  }, [userData, fetchIngestions, fetchAliases]);

  const handlePoll = async () => {
    setPolling(true);
    setMessage(null);
    try {
      const data = await api('/poll', { method: 'POST', body: JSON.stringify({ maxMessages: 50 }) });
      const s = data.summary || {};
      setMessage({ type: 'success', text: `Polled: ${s.scanned} scanned, ${s.ingested} new, ${s.imported} imported, ${s.deduped} dup, ${s.quarantined} quarantined` });
      fetchIngestions();
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setPolling(false);
    }
  };

  const toggleExpand = async (id: number) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (!files[id]) {
      try {
        const data = await api(`/${id}`);
        setFiles(prev => ({ ...prev, [id]: data.files || [] }));
      } catch (e: any) {
        setMessage({ type: 'error', text: e.message });
      }
    }
  };

  const handleRetry = async (id: number) => {
    try {
      const data = await api(`/${id}/retry`, { method: 'POST' });
      const batches = (data.batchIds && data.batchIds.length > 1) ? data.batchIds.join(', ') : data.batchId;
      setMessage({ type: data.imported ? 'success' : 'error', text: data.imported ? `Imported into batch(es): ${batches}` : `Still incomplete. Missing: ${(data.missing || []).join(', ')}` });
      fetchIngestions();
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message });
    }
  };

  const handleQuarantine = async (id: number) => {
    try {
      await api(`/${id}/quarantine`, { method: 'POST', body: JSON.stringify({ reason: 'Manually quarantined' }) });
      fetchIngestions();
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message });
    }
  };

  const handleCreateAlias = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api('/aliases', {
        method: 'POST',
        body: JSON.stringify({
          accountId: Number(aliasForm.accountId),
          alias: aliasForm.alias,
          workerName: aliasForm.workerName,
          allowedSenders: aliasForm.allowedSenders ? aliasForm.allowedSenders.split(',').map(s => s.trim()).filter(Boolean) : null,
          notes: aliasForm.notes || null,
        }),
      });
      setShowAliasForm(false);
      setAliasForm({ accountId: '', alias: '', workerName: 'strauss', allowedSenders: '', notes: '' });
      fetchAliases();
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message });
    }
  };

  const handleDeleteAlias = async (id: number) => {
    if (!window.confirm('Delete this alias?')) return;
    try {
      await api(`/aliases/${id}`, { method: 'DELETE' });
      fetchAliases();
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message });
    }
  };

  const handleToggleAlias = async (a: Alias) => {
    try {
      await api(`/aliases/${a.id}`, { method: 'PUT', body: JSON.stringify({ active: !a.active }) });
      fetchAliases();
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message });
    }
  };

  if (!userData?.isAdmin) return null;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Email Ingestions</h1>
        <button
          onClick={handlePoll}
          disabled={polling}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {polling ? 'Polling…' : 'Poll Gmail Now'}
        </button>
      </div>

      {message && (
        <div className={`mb-4 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {message.text}
        </div>
      )}

      {/* Aliases */}
      <div className="mb-8 bg-white rounded-lg shadow p-4">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-xl font-semibold text-gray-800">Client Aliases</h2>
          <button onClick={() => setShowAliasForm(v => !v)} className="px-3 py-1.5 text-sm bg-gray-800 text-white rounded hover:bg-gray-900">
            {showAliasForm ? 'Cancel' : '+ Add Alias'}
          </button>
        </div>

        {showAliasForm && (
          <form onSubmit={handleCreateAlias} className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-4 p-3 bg-gray-50 rounded">
            <input required placeholder="Account ID" value={aliasForm.accountId} onChange={e => setAliasForm(f => ({ ...f, accountId: e.target.value }))} className="border rounded px-2 py-1" />
            <input required placeholder="alias (e.g. israco)" value={aliasForm.alias} onChange={e => setAliasForm(f => ({ ...f, alias: e.target.value }))} className="border rounded px-2 py-1" />
            <select value={aliasForm.workerName} onChange={e => setAliasForm(f => ({ ...f, workerName: e.target.value }))} className="border rounded px-2 py-1">
              <option value="strauss">strauss</option>
              <option value="carmit">carmit</option>
              <option value="israco">israco</option>
              <option value="admis-pdf">admis-pdf</option>
              <option value="manual">manual</option>
            </select>
            <input placeholder="allowed senders (comma)" value={aliasForm.allowedSenders} onChange={e => setAliasForm(f => ({ ...f, allowedSenders: e.target.value }))} className="border rounded px-2 py-1" />
            <button type="submit" className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700">Save</button>
          </form>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-4">Account</th>
                <th className="py-2 pr-4">Alias</th>
                <th className="py-2 pr-4">Worker</th>
                <th className="py-2 pr-4">Allowed senders</th>
                <th className="py-2 pr-4">Active</th>
                <th className="py-2 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {aliases.map(a => (
                <tr key={a.id} className="border-b last:border-0">
                  <td className="py-2 pr-4">{a.account_name} <span className="text-gray-400">#{a.account_id}</span></td>
                  <td className="py-2 pr-4 font-mono">{a.alias}@spotlock.co</td>
                  <td className="py-2 pr-4">{a.worker_name}</td>
                  <td className="py-2 pr-4 text-gray-500">{(a.allowed_senders || []).join(', ') || 'any'}</td>
                  <td className="py-2 pr-4">
                    <button onClick={() => handleToggleAlias(a)} className={`px-2 py-0.5 rounded text-xs ${a.active ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                      {a.active ? 'active' : 'inactive'}
                    </button>
                  </td>
                  <td className="py-2 pr-4">
                    <button onClick={() => handleDeleteAlias(a.id)} className="text-red-600 hover:underline text-xs">delete</button>
                  </td>
                </tr>
              ))}
              {aliases.length === 0 && (
                <tr><td colSpan={6} className="py-4 text-center text-gray-400">No aliases configured</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ingestions */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-xl font-semibold text-gray-800 mr-auto">Inbox</h2>
          {STATUS_FILTERS.map(s => (
            <button key={s || 'all'} onClick={() => setStatusFilter(s)} className={`px-2.5 py-1 text-xs rounded ${statusFilter === s ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}>
              {s || 'all'}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-8 text-center text-gray-400">Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-4">Received</th>
                  <th className="py-2 pr-4">Account</th>
                  <th className="py-2 pr-4">From</th>
                  <th className="py-2 pr-4">Subject</th>
                  <th className="py-2 pr-4">Files</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Batch</th>
                  <th className="py-2 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {ingestions.map(ing => (
                  <React.Fragment key={ing.id}>
                    <tr className="border-b last:border-0 hover:bg-gray-50">
                      <td className="py-2 pr-4 whitespace-nowrap">{ing.received_at ? new Date(ing.received_at).toLocaleString() : '—'}</td>
                      <td className="py-2 pr-4">{ing.account_name || <span className="text-orange-600">unresolved</span>}</td>
                      <td className="py-2 pr-4 text-gray-600">{ing.from_address}</td>
                      <td className="py-2 pr-4 max-w-xs truncate">{ing.subject}</td>
                      <td className="py-2 pr-4 text-center">{ing.attachment_count}</td>
                      <td className="py-2 pr-4">
                        <span className={`px-2 py-0.5 rounded text-xs ${STATUS_COLORS[ing.status] || 'bg-gray-100 text-gray-600'}`}>{ing.status}</span>
                      </td>
                      <td className="py-2 pr-4">{ing.batch_id ?? '—'}</td>
                      <td className="py-2 pr-4 whitespace-nowrap">
                        <button onClick={() => toggleExpand(ing.id)} className="text-blue-600 hover:underline text-xs mr-2">{expanded === ing.id ? 'hide' : 'files'}</button>
                        {ing.status !== 'imported' && ing.account_id && (
                          <button onClick={() => handleRetry(ing.id)} className="text-green-600 hover:underline text-xs mr-2">retry</button>
                        )}
                        {ing.status !== 'quarantined' && (
                          <button onClick={() => handleQuarantine(ing.id)} className="text-orange-600 hover:underline text-xs">quarantine</button>
                        )}
                      </td>
                    </tr>
                    {expanded === ing.id && (
                      <tr className="bg-gray-50">
                        <td colSpan={8} className="py-2 px-4">
                          {ing.error && <div className="text-red-600 mb-2 text-xs">Error: {ing.error}</div>}
                          <div className="text-xs text-gray-500 mb-1">Window: {ing.window_key} · Worker: {ing.worker_name || '—'} · Msg: {ing.gmail_message_id}</div>
                          <table className="min-w-full text-xs">
                            <thead>
                              <tr className="text-left text-gray-400">
                                <th className="py-1 pr-4">File</th>
                                <th className="py-1 pr-4">Role</th>
                                <th className="py-1 pr-4">Type</th>
                                <th className="py-1 pr-4">Size</th>
                                <th className="py-1 pr-4">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(files[ing.id] || []).map(f => (
                                <tr key={f.id}>
                                  <td className="py-1 pr-4 font-mono">{f.original_filename}</td>
                                  <td className="py-1 pr-4">{f.canonical_role || <span className="text-gray-400">unclassified</span>}</td>
                                  <td className="py-1 pr-4">{f.content_type}</td>
                                  <td className="py-1 pr-4">{f.size_bytes != null ? `${(f.size_bytes / 1024).toFixed(1)} KB` : '—'}</td>
                                  <td className="py-1 pr-4">{f.status}</td>
                                </tr>
                              ))}
                              {(files[ing.id] || []).length === 0 && (
                                <tr><td colSpan={5} className="py-2 text-gray-400">No files</td></tr>
                              )}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {ingestions.length === 0 && (
                  <tr><td colSpan={8} className="py-8 text-center text-gray-400">No ingestions</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailIngestions;
